/* eslint-disable i18next/no-literal-string */
"use client";
import type { AssistantMessage } from "@ui/components/heroes/HeroConversationalAI/ChatAnimation";

import { TypingInput } from "@ui/components/heroes/HeroConversationalAI/TypingInput";
import {
  addUserMessageWithTyping,
  useChatAnimation,
} from "@ui/components/heroes/HeroConversationalAI/useChatAnimation";
import clsx from "clsx";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";

import { ChatHistory } from "./GenerationAnimation/ChatHistory";
import { LeftPanel, Timeline } from "./GenerationAnimation/LeftPanel";
import { Lyrics } from "./GenerationAnimation/Lyrics";
import { GENERATION_SCRIPTS } from "./GenerationAnimation/data";

function PromptBox({
  currentInputValue,
  fullMessage,
  isTyping,
  isSending,
}: {
  currentInputValue: string;
  fullMessage: string | undefined;
  isSending: boolean;
  isTyping: boolean;
}) {
  return (
    <div
      className='relative z-1 flex flex-col gap-16 rounded-[16px] bg-default pb-10 pl-16 pr-10 pt-16 md:rounded-xxl md:pb-16 md:pl-24 md:pr-16 md:pt-24'
      style={{
        background: "linear-gradient(180deg, rgba(255, 255, 255, 0.60) 21.91%, rgba(255, 255, 255, 0.30) 100%)",
        boxShadow: "0 4px 32px 0 rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(0, 0, 0, 0.08)",
      }}
    >
      <div className='f-paragraph-02 text-light'>
        <TypingInput
          currentInputValue={currentInputValue}
          fullMessage={fullMessage}
          isTyping={isTyping}
          placeholder='Enter changes to your song...'
        />
      </div>
      <div className='flex justify-between'>
        <div />
        <m.div
          animate={{ scale: isSending ? 0.9 : 1 }}
          className='f-paragraph-04 rounded-md bg-overlay-dark px-8 py-4 text-light md:f-paragraph-03 md:rounded-[10px] md:px-12 md:py-6'
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          Generate
        </m.div>
      </div>
    </div>
  );
}

interface GenerationAnimationProps {
  /** When true, removes the top border and side gradient borders for use as a CMS block on a white background */
  embeddedInBlock?: boolean;
}

export function GenerationAnimation({ embeddedInBlock = false }: GenerationAnimationProps) {
  const [currentInputValue, setCurrentInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [fullMessage, setFullMessage] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [lyricsContent, setLyricsContent] = useState<string | undefined>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const hasStartedRef = useRef(false);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const addTimeoutKeyToQueue = (key: NodeJS.Timeout) => {
    timeoutRefs.current.push(key);
  };

  // Custom function to handle user message with typing effect
  const addUserMessageWithTypingLocal = (content: string) => {
    return addUserMessageWithTyping({
      addTimeoutKeyToQueue,
      addMessage,
      setCurrentInputValue,
      setFullMessage,
      setIsSending,
      setIsTyping,
    })(content);
  };

  const { messages, addMessage, cleanup, startAutoRotation } = useChatAnimation(
    [],
    GENERATION_SCRIPTS,
    addUserMessageWithTypingLocal,
  );

  // Track current script and step based on messages
  useEffect(() => {
    const assistantMessages = messages.filter(message => message.type === "assistant");
    const userMessages = messages.filter(message => message.type === "user");

    // Find which script we're currently in by matching user messages with script prompts
    for (const script of GENERATION_SCRIPTS) {
      const scriptUserMessages = script.steps.filter(step => step.type === "user").map(step => step.content);
      const currentUserMessages = userMessages.map(msg => msg.content as string);

      // Check if current user messages match the beginning of a script
      const isCurrentScript = scriptUserMessages.every((scriptMsg, index) => currentUserMessages[index] === scriptMsg);

      if (isCurrentScript) {
        // Calculate current step based on assistant messages
        const stepIndex = Math.min(
          assistantMessages.length,
          script.steps.filter(step => step.type === "assistant").length - 1,
        );
        setCurrentStep(stepIndex);
        break;
      }
    }
  }, [messages]);

  // Top-level loading logic
  useEffect(() => {
    const lastGeneration = messages.findLast(message => message.type === "assistant")?.content as AssistantMessage;

    if (lastGeneration?.type === "toolCall") {
      setIsLoading(true);
      setShowResult(false);
      // Don't clear content during loading - keep current lyrics visible

      const loadingDuration = lastGeneration?.loadingDuration || 2000;

      const timer = setTimeout(() => {
        setIsLoading(false);
        setLyricsContent(lastGeneration.resultContent as string);
        setShowResult(true);
      }, loadingDuration);

      return () => clearTimeout(timer);
    } else if (lastGeneration) {
      setIsLoading(false);
      setShowResult(true);
      setLyricsContent(lastGeneration.content as string);
    } else {
      setIsLoading(false);
      setShowResult(false);
      // Keep lyrics visible even when no generation - don't clear content
    }
  }, [messages]);

  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startAutoRotation();
    }
  }, [startAutoRotation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      hasStartedRef.current = false;
      cleanup();
      timeoutRefs.current.forEach(key => clearTimeout(key));
      timeoutRefs.current = [];
    };
  }, [cleanup]);

  return (
    <div
      className={clsx(
        "flex h-[400px] justify-center overflow-hidden pb-40 md:h-[540px]",
        !embeddedInBlock && "border-t border-overlay-darker",
      )}
    >
      <div className='flex w-full min-w-[1000px] max-w-[1440px]'>
        <div className='min-w-0 flex-1'>
          {/* Timeline - always visible */}
          <div className='flex h-52 items-center px-16 py-8 md:px-20 xl:px-40'>
            <Timeline embeddedInBlock={embeddedInBlock} />
          </div>
          {/* Rest of left panel content - always visible */}
          <LeftPanel isLoading={isLoading} />
        </div>
        <div className='relative flex w-[240px] shrink-0 flex-col px-8 pt-28 md:w-[300px] md:px-16 lg:w-[440px] lg:px-20 lg:pt-52'>
          {/* Left gradient border */}
          <div
            className='absolute bottom-0 left-0 top-0 w-1'
            style={{
              background: embeddedInBlock
                ? "linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 50%, transparent 100%)"
                : "linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 50%, transparent 100%)",
            }}
          />
          {/* Right gradient border */}
          <div
            className='absolute bottom-0 right-0 top-0 w-1'
            style={{
              background: embeddedInBlock
                ? "linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 50%, transparent 100%)"
                : "linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 50%, transparent 100%)",
            }}
          />
          <Lyrics
            content={lyricsContent}
            embeddedInBlock={embeddedInBlock}
            isLoading={isLoading}
            showResult={showResult}
          />
          <PromptBox
            currentInputValue={currentInputValue}
            fullMessage={fullMessage}
            isSending={isSending}
            isTyping={isTyping}
          />
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex-1 px-16 md:px-20 xl:px-40'>
            <ChatHistory
              currentStep={currentStep}
              embeddedInBlock={embeddedInBlock}
              isLoading={isLoading}
              userMessages={messages.filter(message => message.type === "user")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
